/**
 * Group Mishor hotel room offers into SERP cards for טיסה + מלון (hotel side).
 * Flights stay separate (LDS) — prices here are hotel-only net/gross from Hubwayz.
 */

import { displayBoardBasisHe } from "./displayBoardBasisHe";
import { normalizeBoardId } from "./filterUnifiedFlightHotelCards";

/** Mishor static returns relative image paths — docs CDN prefix. */
const HUBWAYZ_IMAGE_CDN = "https://cdn-images.innstant-servers.com";
const HUBWAYZ_IMAGE_SIZE = "800x600";

function moneyAmount(node) {
  if (!node || typeof node !== "object") return null;
  const n = Number(node.amount);
  return Number.isFinite(n) ? n : null;
}

function absolutizeHubwayzImageUrl(raw) {
  const path = String(raw || "").trim().replace(/^\/+/, "");
  if (!path) return null;
  if (/^https?:\/\//i.test(path)) return path;
  return `${HUBWAYZ_IMAGE_CDN}/${HUBWAYZ_IMAGE_SIZE}/${path}`;
}

function resolveHotelImage(staticHotel) {
  if (!staticHotel || typeof staticHotel !== "object") return null;
  if (staticHotel.mainImage) {
    return absolutizeHubwayzImageUrl(staticHotel.mainImage);
  }
  const images = Array.isArray(staticHotel.images) ? staticHotel.images : [];
  const mainId = staticHotel.mainImageId != null ? String(staticHotel.mainImageId) : null;
  const main =
    (mainId && images.find((img) => String(img?.id) === mainId)) || images[0] || null;
  if (!main) return null;
  if (typeof main === "string") return absolutizeHubwayzImageUrl(main);
  return absolutizeHubwayzImageUrl(
    main.url || main.src || main.path || main.large || main.medium || main.small
  );
}

/**
 * @param {{ results?: any[], hotelsById?: Record<string, any> }} mishorData
 * @param {{ dateRangeLabel?: string, nights?: number }} meta
 */
export function mapHubwayzToFlightHotelCards(mishorData, meta = {}) {
  const results = Array.isArray(mishorData?.results) ? mishorData.results : [];
  const hotelsById = mishorData?.hotelsById || {};
  /** @type {Map<string, any>} */
  const byHotel = new Map();

  for (const offer of results) {
    const items = Array.isArray(offer?.items) ? offer.items : [];
    const first = items[0] || {};
    const hotelId = first.hotelId != null ? String(first.hotelId) : null;
    if (!hotelId) continue;

    const net = moneyAmount(offer.netPrice);
    const gross = moneyAmount(offer.price);
    const currency = offer?.netPrice?.currency || "";
    const prev = byHotel.get(hotelId);
    if (prev && Number.isFinite(prev._sortNet) && Number.isFinite(net) && prev._sortNet <= net) {
      prev.roomOptionsCount = (prev.roomOptionsCount || 1) + 1;
      continue;
    }

    const staticHotel = hotelsById[hotelId] || {};
    const name =
      staticHotel.name ||
      staticHotel.Name ||
      staticHotel.hotelName ||
      `Hotel ${hotelId}`;
    const stars = Number(staticHotel.stars || staticHotel.Stars || staticHotel.rating || 0) || 0;
    const city =
      staticHotel.city ||
      staticHotel.City ||
      staticHotel.destinationName ||
      meta.cityLabel ||
      "";
    const country = staticHotel.country || staticHotel.Country || meta.countryLabel || "";
    const imageUrl = resolveHotelImage(staticHotel) || null;

    // Hubwayz net/price = stay total for the searched room (not per person).
    const hotelNetTotal = Number.isFinite(net) ? net : null;
    const pax = Math.max(1, Number(meta.paxCount) || 2);
    const hotelPerPerson =
      hotelNetTotal != null ? hotelNetTotal / pax : null;

    byHotel.set(hotelId, {
      id: `hwz-${hotelId}`,
      provider: "hubwayz",
      hotelId,
      hotelName: name,
      stars,
      city,
      country,
      destinationId: meta.destinationId,
      boardId: normalizeBoardId(first.board),
      boardLabel: displayBoardBasisHe(first.board),
      roomType: first.name || first.category || "חדר",
      nights: meta.nights || null,
      dateRangeLabel: meta.dateRangeLabel || "",
      currency,
      paxCount: pax,
      hotelNetTotal,
      /** Agent-facing combined net/person — flight+HR merged in attachFlightStrip. */
      agentNetPerPerson: hotelPerPerson,
      agentNetPerPersonIls: null,
      pricePerPerson: hotelPerPerson,
      totalForTwo: hotelPerPerson != null ? hotelPerPerson * pax : null,
      immediateConfirm: String(offer.confirmation || "").toLowerCase() === "immediate",
      directFlight: false,
      transferIncluded: false,
      highCommission: !!offer.commissionable,
      flightOptionsCount: 0,
      roomOptionsCount: (prev?.roomOptionsCount || 0) + 1,
      baggage: { none: true, bullets: [] },
      imageUrl,
      outbound: null,
      inbound: null,
      hubwayzOfferCode: offer.code,
      hubwayzSearchRequest: meta.searchRequest || null,
      _sortNet: Number.isFinite(net) ? net : Number.POSITIVE_INFINITY,
    });
  }

  return Array.from(byHotel.values())
    .sort((a, b) => a._sortNet - b._sortNet)
    .map(({ _sortNet, ...rest }) => rest);
}
